import { Button } from '@cw-ui/form-components';
import { Typography } from '@mui/material';
import { userManager } from 'cw-ui-core';
import { useEffect, useMemo, useRef } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { pageKeys } from 'src/asio-neon-components/Constants';
import { getRouteFromPageKey } from 'src/asio-neon-components/Utils';
import NonLoggedInAppBanner from 'src/components/AppBanner/NonLoggedInAppBanner';
import NavigationTabsContainer from 'src/components/NavigationTabs/NavigationTabsContainer';
import getScreenFactory from 'src/components/ScreenFactory';
import { SINGIN_REDIRECT_URL } from 'src/configs/oidcConfig';
import { ASOI_PLATFORM_APP_ID, PERMISSIONS } from 'src/constants';
import { fetchAppRolesMapping } from 'src/features/current-user/slice';
import Locale from 'src/i18n';
import { LogoutSuccess } from 'src/pages/LogoutSuccess/LogoutSuccess';
import { NoMatch } from 'src/pages/NoMatch/NoMatch';
import { getCurrentUserIdToken } from 'src/services/ApiService';
import css from 'src/styles/App.scss';
import emitter, { EVENTS } from 'src/util/emitter';
import { Features, hasFeature } from 'src/util/featureToggles';
import { checkIfUserHaveGivenPermissions } from 'src/util/Util';
import AppBanner from '../components/AppBanner/AppBanner';
import AsioNeonResponsiveThemeProvider from '../theme/AsioNeonResponsiveThemeProvider';
import AppLoader from 'src/components/AppLoader/AppLoader';
import BulkUserImport from 'src/features/bulk-users-import/BulkUserImport';
import { bulkImportUsersSliceId } from 'src/features/bulk-users-import/slice';

const inIframe = window.self !== window.top;

const App = appProps => {
    const {
        onDidMount,
        appInitialized,
        currentUserId,
        showApplicationRoleManagementTab,
        openNotifications,
        numberOfUsersNeedingApproval,
        onUsersApprovalButtonClick,
        originPartnerId,
        notificationCount,
    } = appProps;
    const { formatMessage } = appProps.intl;
    const hasNewIdentityRolesFeature = hasFeature(Features.NEW_IDENTITY_ROLES_FEATURE);
    const hasNewUsersFeature = hasFeature(Features.NEW_USERS_FEATURE);
    const hasNewProductsFeature = hasFeature(Features.NEW_PRODUCTS_FEATURE);
    const hasBulkUserImportFeature = hasFeature(Features.BULK_USER_ASSIGNMENT);
    const isbulkImportOpen = useSelector(state => state[bulkImportUsersSliceId]?.isbulkImportOpen);

    const shouldRenderUsersRoute = useMemo(
        () =>
            checkIfUserHaveGivenPermissions(
                [
                    PERMISSIONS.USERS_ENABLE,
                    PERMISSIONS.USERS_EMAIL_RESEND,
                    PERMISSIONS.USERS_VIEW,
                    PERMISSIONS.USERS_PASSWORD_RESET,
                ],
                true
            ),
        []
    );

    const dispatch = useDispatch();
    const isLoggedOutStateRef = useRef(false);

    const shouldRenderIdentityRolesRoute = useMemo(() => checkIfUserHaveGivenPermissions([PERMISSIONS.ROLES_VIEW]), []);

    const logout = () => {
        if (isLoggedOutStateRef.current) {
            return;
        }
        sessionStorage.clear();
        const idToken = getCurrentUserIdToken();
        // window.parent?.postMessage({ type: 'logout' }, '*'); No need to tell parent to logout /checksssion will do
        userManager().signoutRedirect({
            id_token_hint: idToken,
        });
        isLoggedOutStateRef.current = true;
    };

    useEffect(() => {
        onDidMount();
    }, [onDidMount]);

    useEffect(() => {
        // fetch app roles mapping for current user, currently fetching only for ASOI platform app
        dispatch(
            fetchAppRolesMapping({
                currentUserId,
                appId: ASOI_PLATFORM_APP_ID,
            })
        );
        emitter.on(EVENTS.APP_ROLES_MAPPING_UPDATED, payload => {
            const { forUser } = payload || {};
            if (forUser && forUser === currentUserId) {
                dispatch(
                    fetchAppRolesMapping({
                        currentUserId,
                        appId: ASOI_PLATFORM_APP_ID,
                    })
                );
            }
        });

        const appLogoutHandler = () => {
            console.log('App -> appLogoutHandler -> inIframe:', inIframe);
            if (inIframe) {
                logout();
            }
        };
        const ac = new AbortController();
        const { signal } = ac;

        window.addEventListener('beforeunload', appLogoutHandler, { signal });
        window.addEventListener('pagehide', appLogoutHandler, { signal });
        window.addEventListener('unload', appLogoutHandler, { signal });

        return () => {
            ac.abort();
            sessionStorage.clear();
        };
    }, []);

    if (!appInitialized) {
        return <AppLoader />;
    }

    if (!appProps.isAuthorized) {
        return (
            <AsioNeonResponsiveThemeProvider>
                <NonLoggedInAppBanner />
                <div className={css.wrap}>
                    <div className={css.noAccessContainer}>
                        <Typography variant="h3" mb={2}>
                            Oops!
                        </Typography>
                        <Typography variant="body1" className={css.accessDenied}>
                            {formatMessage(Locale.accessDenied)}
                        </Typography>
                        <Button className={css.logout} automationName="logout" onClick={logout}>
                            {formatMessage(Locale.logout)}
                        </Button>
                    </div>
                </div>
            </AsioNeonResponsiveThemeProvider>
        );
    }

    return (
        <BrowserRouter
            future={{
                v7_relativeSplatPath: true,
            }}>
            <AsioNeonResponsiveThemeProvider>
                <AppBanner
                    openNotifications={openNotifications}
                    numberOfUsersNeedingApproval={numberOfUsersNeedingApproval}
                    onUsersApprovalButtonClick={onUsersApprovalButtonClick}
                    notificationCount={notificationCount}
                />
                {hasBulkUserImportFeature && isbulkImportOpen && <BulkUserImport />}
                <Routes>
                    {hasNewUsersFeature && (
                        <Route
                            exact
                            path={getRouteFromPageKey(pageKeys.ADD_USER)}
                            element={getScreenFactory('AddUser')}
                        />
                    )}
                    <Route exact path="/authentication/duo-mfa/create" element={getScreenFactory('AddDuoMFA')} />
                    <Route exact path="/authentication/duo-mfa/edit/:scheme" element={getScreenFactory('EditDuoMFA')} />
                    <Route
                        exact
                        path="/authentication/login-provider/create"
                        element={getScreenFactory('AddExternalIdP')}
                    />
                    <Route
                        exact
                        path="/authentication/login-provider/edit/:scheme"
                        element={getScreenFactory('EditExternalIdP')}
                    />
                </Routes>

                <div className={css.wrap}>
                    <NavigationTabsContainer onNavigate={appProps.onNavigate} />
                    <Routes>
                        {shouldRenderUsersRoute && (
                            <>
                                <Route exact path="/" element={getScreenFactory('Users')} />
                                <Route exact path="/users" element={getScreenFactory('Users')} />
                                {originPartnerId && (
                                    <Route exact path="/guest-users" element={getScreenFactory('GuestUsers')} />
                                )}
                            </>
                        )}
                        <Route exact path="/authentication" element={getScreenFactory('NewAuthentication')} />
                        <Route path="/invites" element={getScreenFactory('PendingInvites')} />
                        <Route path="/importedUsers" element={getScreenFactory('ImportedUsers')} />
                        {/* <Route path="/pendingInvites" element={getScreenFactory('PendingInvites')} /> */}
                        <Route path="/products" element={getScreenFactory('Products')} />
                        {hasNewProductsFeature && (
                            <Route exact path="/products/v2" element={getScreenFactory('ProductsV2')} />
                        )}
                        <Route path="/subscribers" element={getScreenFactory('Subscribers')} />
                        <Route path="/registrationToken" element={getScreenFactory('Products')} />
                        {shouldRenderIdentityRolesRoute && <Route path="/roles" element={getScreenFactory('Roles')} />}
                        <Route path="/support" element={getScreenFactory('Support')} />
                        <Route
                            exact
                            path="/configureApplication/:clientId"
                            element={getScreenFactory('ConfigureApplication')}
                        />
                        <Route exact path="/configureApplication" element={getScreenFactory('ConfigureApplication')} />
                        <Route exact path="/confirmApplication" element={getScreenFactory('ConfirmApplication')} />
                        {showApplicationRoleManagementTab && (
                            <Route exact path="/application-roles" element={getScreenFactory('ApplicationRoles')}>
                                <Route path="permissions" element={<></>} />
                                <Route path="content-assignment" element={<></>} />
                                <Route path="users" element={<></>} />
                            </Route>
                        )}
                        {hasNewIdentityRolesFeature && (
                            <Route path="/identityRoles" element={getScreenFactory('NewIdentityRoles')} />
                        )}

                        {hasNewUsersFeature && <Route exact path="/usersNew" element={getScreenFactory('NewUsers')} />}
                        <Route path="/logout-success" element={<LogoutSuccess />} />
                        <Route path={SINGIN_REDIRECT_URL} element={<Navigate replace to="/" />} />
                        <Route path="*" element={<NoMatch />} />
                    </Routes>
                </div>
            </AsioNeonResponsiveThemeProvider>
        </BrowserRouter>
    );
};

export default injectIntl(App);
